import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ActivityType, AnalysisReport } from "@lifelog/shared";
import { api, ApiError, isOfflineError } from "../lib/api.js";

const RANGES = [30, 90, 365] as const;

function estimateUsd(rangeDays: number, typeCount: number): { inputTokens: number; usd: number } {
  const inputTokens = 2500 + typeCount * 120 + Math.min(rangeDays, 90) * 80;
  const outputTokens = 2500;
  const usd = (inputTokens / 1_000_000) * 3 + (outputTokens / 1_000_000) * 15;
  return { inputTokens, usd };
}

export function AnalyzePane({
  types,
  onCreated,
}: {
  types: ActivityType[];
  onCreated: (report: AnalysisReport & { markdown: string }) => void;
}) {
  const [rangeDays, setRangeDays] = useState<(typeof RANGES)[number]>(90);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(types.map((t) => t.activityTypeId)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<(AnalysisReport & { markdown: string }) | null>(null);
  const [copied, setCopied] = useState(false);

  const scoped = useMemo(() => types.filter((t) => selected.has(t.activityTypeId)), [types, selected]);
  const estimate = estimateUsd(rangeDays, scoped.length);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const report = await api<AnalysisReport & { markdown: string }>("/analyze", {
        method: "POST",
        json: {
          rangeDays,
          activityTypeIds: scoped.length === types.length ? undefined : [...selected],
        },
      });
      setResult(report);
      onCreated(report);
    } catch (err) {
      if (isOfflineError(err)) setError("You're offline. Try again when you're back online.");
      else setError(err instanceof ApiError ? err.message : "Analysis failed.");
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!result?.markdown) return;
    await navigator.clipboard.writeText(result.markdown);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <section className="space-y-4">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">New analysis</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Runs over pre-aggregated stats, not raw history, so a 90-day run stays around a few cents.
      </p>

      <div className="inline-flex max-w-full flex-nowrap overflow-x-auto rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800">
        {RANGES.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRangeDays(n)}
            className={[
              "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
              rangeDays === n
                ? "bg-(--accent) text-(--accent-contrast) shadow-sm"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
            ].join(" ")}
            aria-pressed={rangeDays === n}
          >
            Last {n}d
          </button>
        ))}
      </div>

      <div>
        <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
          Activities
          <button type="button" className="text-(--accent) hover:underline" onClick={() => setSelected(new Set(types.map((t) => t.activityTypeId)))}>
            all
          </button>
          <button type="button" className="text-(--accent) hover:underline" onClick={() => setSelected(new Set())}>
            none
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {types.map((t) => (
            <button
              key={t.activityTypeId}
              type="button"
              onClick={() => toggle(t.activityTypeId)}
              className={[
                "rounded-full border px-2 py-1 text-xs",
                selected.has(t.activityTypeId)
                  ? "border-(--accent) bg-(--accent) text-(--accent-contrast)"
                  : "border-gray-300 text-gray-600 dark:border-gray-700 dark:text-gray-400",
              ].join(" ")}
            >
              {t.shortName}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Estimated ~{estimate.inputTokens.toLocaleString()} input tokens · ${estimate.usd.toFixed(3)} at Sonnet 5 list price
      </p>

      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">{error}</div>
      )}

      <button
        type="button"
        onClick={() => void run()}
        disabled={busy || scoped.length === 0}
        className="rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
      >
        {busy ? "Running…" : "Run analysis"}
      </button>

      {result && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{result.title}</h3>
            <button
              type="button"
              onClick={() => void copy()}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {result.stopReason === "max_tokens" && (
            <p className="mb-2 text-xs text-amber-700 dark:text-amber-400">This report may be truncated.</p>
          )}
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed text-gray-800 dark:text-gray-200">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.markdown}</ReactMarkdown>
          </div>
        </div>
      )}
    </section>
  );
}
